import { Box, Button } from '@factorialco/deprecated-design-system'
import { UpdateSerialSequenceDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { SerialSequencesPreviewBox } from 'modules/finance/components/SerialSequences/PreviewBox'

import { SerialSequenceLoadingModal } from '../SerialSequenceLoadingModal'
import { SerialSequenceNotFoundModal } from '../SerialSequenceNotFoundModal'
import { useSerialSequence } from '../useSerialSequence'

interface SerialSequenceUpdateNameModalFormValues {
  name: string
}

export const SerialSequenceUpdateNameModal = ({
  isOpen,
  onClose,
  refetchQuery,
  serialSequenceId,
}: {
  isOpen: boolean
  onClose: () => void
  refetchQuery?: string
  serialSequenceId: number
}) => {
  const { serialSequence, loading, notFound } = useSerialSequence(serialSequenceId, isOpen)

  const [updateSerialSequence] = useMutation(UpdateSerialSequenceDocument, {
    extractorFn: (data) =>
      data.financeMutations.updateNameFinanceSerialSequenceSalesInvoiceSequence,
    refetchQueries: refetchQuery ? [refetchQuery] : undefined,
  })

  const form = useForm<SerialSequenceUpdateNameModalFormValues>({
    defaultValues: {
      name: '',
    },
    validateOnChange: true,
    validate: (data) => {
      const errors: FormErrors<SerialSequenceUpdateNameModalFormValues> = {}

      if (!data.name) {
        errors.name = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit: async (values) => {
      const result = await updateSerialSequence({
        variables: {
          id: serialSequenceId,
          name: values.name,
        },
      })

      const handled = handleResult(result, (parsedErrors) => {
        if (parsedErrors.name) {
          form.setErrors({
            name: parsedErrors.name,
          })
        } else {
          app.setFlash({
            type: 'wrong',
            message: parsedErrors.errors ?? i18n.t('errors.generic_error'),
          })
        }
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.serial_sequences.modal.update_success'),
          time: 2000,
        })
        userClose()
        return { success: true, data: values }
      } else {
        return {
          errors: [],
          success: false,
        }
      }
    },
  })

  const userClose = () => {
    form.resetForm()
    onClose()
  }

  if (!isOpen) return null

  if (loading) {
    return (
      <SerialSequenceLoadingModal
        title={i18n.t('finance.serial_sequences.modal.edit_sequence_title')}
        onClose={onClose}
      />
    )
  }

  if (notFound) {
    return (
      <SerialSequenceNotFoundModal
        title={i18n.t('finance.serial_sequences.modal.edit_sequence_title')}
        serialSequenceId={serialSequenceId}
        onClose={onClose}
      />
    )
  }

  if (!serialSequence) {
    return null
  }

  return (
    <Modal
      illustration="input-field"
      title={i18n.t('finance.serial_sequences.modal.edit_sequence_title')}
      description={i18n.t('finance.serial_sequences.modal.edit_sequence_description')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={userClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit>{i18n.t('common.save')}</Button>
        </>
      }
    >
      <Box gap="s16">
        <SerialSequencesPreviewBox preview={serialSequence.humanReadablePreview} />
        <TextField
          label={i18n.t('finance.serial_sequences.modal.sequence_name_label')}
          value={form.values.name || serialSequence.name}
          onChange={(newValue) => form.setFieldValue('name', newValue)}
          error={form.touched.name ? form.errors.name : undefined}
          placeholder={serialSequence.name}
        />
      </Box>
    </Modal>
  )
}
